import { type ReactiveHead, useHead } from '@unhead/vue'
import { computed } from 'vue'
import { type RouteLocationNamedRaw, type RouteLocationRaw, useRouter } from 'vue-router'

import { useLocalizedRouters } from '@/composables/localized-routers'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import type { AvailableLocale } from '@/i18n/types'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY, pageQuery } from '@/router/query-params'
import type { CategoryInfo } from '@/types'

const TOP_GAMES_COUNT = 20

type UseListingSeoParams = {
    info: () => Pick<CategoryInfo, 'seoPicture' | 'metaTitle' | 'metaDescription' | 'localizations'> | undefined
    totalPages: () => number | undefined
    routeName: () => string
    page: () => number
    titles: () => { templateTitle: string; defaultMetaTitle: string; defaultMetaDescription: string }
    breadcrumbs: () => { title: string; href: string | RouteLocationRaw }[]
    games: () => { hru: string; title: string; preview?: string }[]
}

export function useListingSeo({ info, totalPages, routeName, page, titles, breadcrumbs, games }: UseListingSeoParams) {
    const tr = useTr()
    const router = useRouter()
    const { Organization, Founder, WebSite, WebSiteId, getFullLink, getInLanguage } = useSchemaOrg()
    const { getCanonicalAndAlternates, getLocalizedUrls } = useLocalizedRouters()

    const templateTitle = computed(() => titles().templateTitle)
    const metaTitle = computed(() => info()?.metaTitle || titles().defaultMetaTitle)
    const metaDescription = computed(() => info()?.metaDescription || titles().defaultMetaDescription)

    const pageStr = computed(() => (page() > 1 ? ` | ${tr('categoryPage.page', { page: String(page()) })}` : ''))

    const breadcrumbsSchema = computed(() => {
        if (!breadcrumbs().length) {
            return {}
        }

        const breadcrumbItems = breadcrumbs().map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'Thing',
                '@id': getFullLink(item.href),
                name: item.title,
            },
        }))

        return {
            breadcrumb: {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbItems,
            },
        }
    })

    const mainEntity = computed(() => {
        const currentPageGames = games().slice(0, TOP_GAMES_COUNT)

        if (!currentPageGames.length || page() > 1) {
            return {}
        }

        const gameItems = currentPageGames.map((game, index) => ({
            '@type': 'ListItem',
            position: 1 + index,
            item: {
                '@type': ['VideoGame', 'WebApplication'],
                name: game.title,
                url: getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.hru } }),
                operatingSystem: ['Windows', 'Chrome OS', 'Linux', 'MacOS', 'Android', 'iOS'],
                applicationCategory: 'GameApplication',
                ...(game.preview ? { image: { '@type': 'ImageObject', url: game.preview } } : {}),
            },
        }))

        return {
            mainEntity: {
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: templateTitle.value,
                numberOfItems: currentPageGames.length,
                itemListElement: gameItems,
            },
        }
    })

    const organizationSchema = computed(() => {
        const infoValue = info()
        if (!infoValue) {
            return {}
        }

        const currentRouteWithPagination = getFullLink({
            name: routeName(),
            query: pageQuery(page()),
        })
        const lang = getInLanguage()
        const categoryPicture = infoValue.seoPicture
            ? {
                  '@type': 'ImageObject',
                  url: infoValue.seoPicture,
                  contentUrl: infoValue.seoPicture,
                  width: 1200,
                  height: 1200,
                  caption: tr('categoryPage.seo_caption', { title: templateTitle.value }),
                  name: `${templateTitle.value} Cover Image`,
                  inLanguage: lang,
              }
            : undefined
        const primaryCategoryPicture = categoryPicture ? { ...categoryPicture, representativeOfPage: true } : undefined

        return {
            '@context': 'https://schema.org',
            '@graph': [
                Organization.value,
                Founder.value,
                WebSite.value,
                {
                    '@type': 'CollectionPage',
                    '@id': `${currentRouteWithPagination}#CollectionPage`,
                    url: currentRouteWithPagination,
                    name: `${metaTitle.value}${pageStr.value}`,
                    description: `${metaDescription.value}${pageStr.value}`,
                    inLanguage: lang,
                    ...(primaryCategoryPicture ? { primaryImageOfPage: primaryCategoryPicture } : {}),
                    ...(categoryPicture ? { image: categoryPicture } : {}),
                    ...(infoValue.seoPicture ? { thumbnailUrl: infoValue.seoPicture } : {}),
                    isPartOf: {
                        '@id': WebSiteId.value,
                    },
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: {
                            '@type': 'EntryPoint',
                            urlTemplate: getFullLink({
                                name: ROUTE_NAMES.SEARCH,
                                query: { [SSR_QUERY.SEARCH_QUERY]: '{search_term_string}' },
                            }),
                        },
                        'query-input': 'required name=search_term_string',
                    },
                    ...breadcrumbsSchema.value,
                    ...mainEntity.value,
                },
            ],
        }
    })

    function mapLocalizedRoute(route: RouteLocationNamedRaw, locale: AvailableLocale): RouteLocationNamedRaw {
        const localizedHru = info()?.localizations?.find((l) => l.lang === locale)?.hru
        if (!localizedHru) {
            return route
        }
        return {
            ...route,
            params: {
                ...route.params,
                hru: localizedHru,
            },
            query: pageQuery(page()),
        }
    }

    const pageMeta = computed(() => {
        const total = totalPages()
        const infoValue = info()
        if (total === undefined || !infoValue) {
            return {}
        }

        const links: ReactiveHead['link'] = [...getCanonicalAndAlternates({ mapRoute: mapLocalizedRoute })]

        if (page() > 1) {
            links.push({
                rel: 'prev',
                href: router.resolve({ name: routeName(), query: pageQuery(page() - 1) }).href,
            })
        }
        if (page() < total) {
            links.push({
                rel: 'next',
                href: router.resolve({ name: routeName(), query: pageQuery(page() + 1) }).href,
            })
        }

        return {
            title: () => `${metaTitle.value}${pageStr.value}`,
            script: [
                {
                    type: 'application/ld+json',
                    innerHTML: organizationSchema.value,
                },
            ],
            meta: [
                {
                    name: 'title',
                    content: `${metaTitle.value}${pageStr.value}`,
                },
                {
                    name: 'description',
                    content: `${metaDescription.value}${pageStr.value}`,
                },
                {
                    property: 'og:title',
                    content: `${metaTitle.value}${pageStr.value}`,
                },
                {
                    property: 'og:description',
                    content: `${metaDescription.value}${pageStr.value}`,
                },
                ...(infoValue.seoPicture
                    ? [
                          { property: 'og:image', content: infoValue.seoPicture },
                          {
                              property: 'og:image:alt',
                              content: tr('categoryPage.seo_caption', { title: templateTitle.value }),
                          },
                          { name: 'twitter:image', content: infoValue.seoPicture },
                          {
                              name: 'twitter:image:alt',
                              content: tr('categoryPage.seo_caption', { title: templateTitle.value }),
                          },
                      ]
                    : []),
                {
                    name: 'twitter:title',
                    content: `${metaTitle.value}${pageStr.value}`,
                },
                {
                    name: 'twitter:description',
                    content: `${metaDescription.value}${pageStr.value}`,
                },
            ],
            link: links,
        }
    })

    const localeData = computed(() => ({
        languageLinks: getLocalizedUrls({ mapRoute: mapLocalizedRoute }),
    }))

    useHead(pageMeta)

    return { localeData }
}
